import React, { useEffect, useMemo, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import {
  // data
  QUIZ_DISCIPLINES,
  QUIZ_LEVELS,
  BASE_XP_PER_CORRECT,
  LEVEL_THRESHOLDS,
  // helpers
  loadXp,
  saveXp,
  loadStats,
  saveStats,
  pct,
  getLevelFromXp,
  getRandomQuestions,
  fetchAllQuizQuestions,
  getCachedQuizQuestions,
} from "../../../api/quiz";
import "./quizPage.css";

// Animation variants for question transitions
const questionVariants = {
  initial: { opacity: 0, x: 50, scale: 0.98 },
  animate: { opacity: 1, x: 0, scale: 1 },
  exit: { opacity: 0, x: -50, scale: 0.98 }
};

const optionVariants = {
  initial: { opacity: 0, y: 20 },
  animate: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.3, ease: "easeOut" }
  }),
  exit: { opacity: 0, y: -10, transition: { duration: 0.15 } }
};

const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

const SetupStep = ({ onStart }) => {
  const [discipline, setDiscipline] = useState(QUIZ_DISCIPLINES[0].id);
  const [level, setLevel] = useState(QUIZ_LEVELS[0].id);

  // Icon mapping for disciplines
  const disciplineIcons = {
    fiqh: 'bi-journal-bookmark',
    aqida: 'bi-heart',
    hadith: 'bi-chat-quote',
    sirah: 'bi-person-badge'
  };

  // Icon and color mapping for levels
  const levelConfig = {
    easy: { icon: 'bi-star', color: '#22c55e' },
    medium: { icon: 'bi-star-half', color: '#f59e0b' },
    hard: { icon: 'bi-stars', color: '#ef4444' }
  };

  return (
    <div className="blog-grid-wrapper pt-110 quiz-template">
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="quiz-card qt-card" style={{ minHeight: 'calc(100vh - 110px)', display: 'flex', flexDirection: 'column' }}>
              <div className="qt-card-head">
                <span className="qt-head-title">Préparer le quiz</span>
              </div>
              <div className="qt-question-box qt-start-box" style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column' }}>
                
                {/* Hero Section */}
                <motion.div 
                  className="qt-setup-hero"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="qt-setup-icon">
                    <i className="bi bi-mortarboard-fill" />
                  </div>
                  <h2 className="qt-setup-title">Testez vos connaissances</h2>
                  <p className="qt-setup-subtitle">Choisissez une discipline et un niveau pour commencer</p>
                </motion.div>

                {/* Discipline Selection */}
                <motion.div 
                  className="qt-setup-section"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                >
                  <label className="qt-setup-label">
                    <i className="bi bi-book me-2" />
                    Choisir une discipline
                  </label>
                  <div className="qt-discipline-grid">
                    {QUIZ_DISCIPLINES.map((d, idx) => (
                      <motion.button
                        key={d.id}
                        type="button"
                        className={`qt-discipline-card ${discipline === d.id ? "is-active" : ""}`}
                        onClick={() => setDiscipline(d.id)}
                        aria-pressed={discipline === d.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3 + idx * 0.1, duration: 0.3 }}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="qt-discipline-icon">
                          <i className={`bi ${disciplineIcons[d.id] || 'bi-book'}`} />
                        </div>
                        <span className="qt-discipline-label">{d.label}</span>
                        {discipline === d.id && (
                          <motion.div 
                            className="qt-discipline-check"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 500 }}
                          >
                            <i className="bi bi-check-lg" />
                          </motion.div>
                        )}
                      </motion.button>
                    ))}
                  </div>
                </motion.div>

                {/* Level Selection */}
                <motion.div 
                  className="qt-setup-section"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.4 }}
                >
                  <label className="qt-setup-label">
                    <i className="bi bi-speedometer2 me-2" />
                    Choisir un niveau
                  </label>
                  <div className="qt-level-grid">
                    {QUIZ_LEVELS.map((l, idx) => {
                      const config = levelConfig[l.id] || { icon: 'bi-star', color: '#6b7280' };
                      return (
                        <motion.button
                          key={l.id}
                          type="button"
                          className={`qt-level-card ${level === l.id ? "is-active" : ""}`}
                          onClick={() => setLevel(l.id)}
                          aria-pressed={level === l.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.5 + idx * 0.1, duration: 0.3 }}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          style={{ '--level-color': config.color }}
                        >
                          <i className={`bi ${config.icon} qt-level-icon`} />
                          <span className="qt-level-label">{l.label}</span>
                        </motion.button>
                      );
                    })}
                  </div>
                </motion.div>

                {/* Start Button */}
                <motion.div 
                  className="qt-setup-actions"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7, duration: 0.3 }}
                >
                  <motion.button 
                    className="qt-start-btn"
                    onClick={() => onStart({ discipline, level })}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <i className="bi bi-play-fill me-2" />
                    Lancer le quiz
                    <i className="bi bi-arrow-right ms-2" />
                  </motion.button>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


const QuizStep = ({ discipline, level, onFinish, onQuit }) => {
  // Confetti instance bound to a dedicated full-screen canvas for correct centering
  const confettiRef = useRef(null);
  const allQuestions = getCachedQuizQuestions();
  const questions = useMemo(
    () => getRandomQuestions(allQuestions, discipline, level, 10),
    [allQuestions, discipline, level]
  );
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selected, setSelected] = useState(null); // option id
  const [revealed, setRevealed] = useState(false);
  const [earnedXp, setEarnedXp] = useState(0);
  const [sessionCorrect, setSessionCorrect] = useState(0); // Track correct answers for THIS quiz session

  const [totalXp, setTotalXp] = useState(loadXp());
  const [stats, setStats] = useState(loadStats());
  const validateBtnControls = useAnimation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Initialize a dedicated overlay canvas once for centered/confident rendering
  useEffect(() => {
    let canvas = document.getElementById("quiz-confetti-canvas");
    if (!canvas) {
      canvas = document.createElement("canvas");
      canvas.id = "quiz-confetti-canvas";
      canvas.className = "quiz-confetti-canvas";
      document.body.appendChild(canvas);
    }
    try {
      // Disable worker to avoid CSP/path issues that can silently block rendering
      confettiRef.current = confetti.create(canvas, { resize: true, useWorker: false });
    } catch (_) {
      confettiRef.current = null;
    }
    // No cleanup to keep a single shared canvas across quiz steps
  }, []);

  const ensureConfettiInstance = () => {
    if (confettiRef.current) return confettiRef.current;
    let canvas = document.getElementById("quiz-confetti-canvas");
    if (!canvas) {
      canvas = document.createElement("canvas");
      canvas.id = "quiz-confetti-canvas";
      canvas.className = "quiz-confetti-canvas";
      document.body.appendChild(canvas);
    }
    try {
      confettiRef.current = confetti.create(canvas, { resize: true, useWorker: false });
    } catch {
      confettiRef.current = null;
    }
    return confettiRef.current;
  };

  const fireCenteredConfetti = () => {
    const instance = ensureConfettiInstance();
    // Adapt sizes and density by viewport: smaller on desktop, slightly larger on mobile
    const getViewportTuning = () => {
      try {
        const w = window.innerWidth || 1024;
        if (w >= 1200) return { s1: 0.35, s2: 0.3, dens: 1.1 };  // dense but small on large screens
        if (w >= 992)  return { s1: 0.40, s2: 0.35, dens: 1.0 };
        if (w <= 576)  return { s1: 0.58, s2: 0.52, dens: 0.7 }; // reduce particles on small devices
        return { s1: 0.5, s2: 0.45, dens: 0.9 };
      } catch {
        return { s1: 0.45, s2: 0.4, dens: 0.9 };
      }
    };
    const { s1, s2, dens } = getViewportTuning();

    // Helper to fire a burst with overrides
    const fireBurst = (opts) => {
      try { instance && instance(opts); } catch {}
      try { confetti(opts); } catch {}
    };

    // Upward fountain from bottom with visible fall back
    const baseUp = Math.round(120 * dens); // per sub-burst
    const baseTrail = Math.round(100 * dens);
    const colors = ["#2d373c", "#00b894", "#ffeaa7", "#6c5ce7", "#0984e3"];
    const bottomY = 0.98;
    const origins = [0.10, 0.25, 0.40, 0.50, 0.60, 0.75, 0.90].map((x) => ({ x, y: bottomY }));

    // Wave A: powerful upward blasts
    origins.forEach((o, idx) => {
      const angle = 85 + (idx % 3) * 5; // 85, 90, 95 for a fan effect
      const drift = (idx % 2 === 0 ? -0.4 : 0.4);
      fireBurst({
        particleCount: baseUp,
        angle,
        spread: 60,
        startVelocity: 62,
        gravity: 1.15,
        decay: 0.92,
        drift,
        origin: o,
        scalar: s1,
        colors,
      });
    });

    // Wave B: trailing burst for density and longer fall
    setTimeout(() => {
      origins.forEach((o, idx) => {
        const angle = 88 + (idx % 3) * 4; // slightly tighter
        const drift = (idx % 2 === 0 ? 0.35 : -0.35);
        fireBurst({
          particleCount: baseTrail,
          angle,
          spread: 70,
          startVelocity: 48,
          gravity: 1.20,
          decay: 0.91,
          drift,
          origin: o,
          scalar: s2,
          colors,
        });
      });
    }, 220);
  };

  const scheduleConfetti = () => {
    try { window.requestAnimationFrame(() => fireCenteredConfetti()); } catch { fireCenteredConfetti(); }
  };

  const q = questions[currentIdx];
  if (!q) {
    return (
      <div className="blog-grid-wrapper pt-110">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="section-head">
                <h3>Chargement des questions…</h3>
                <p>Merci de patienter.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  const isCorrect = selected
    ? q.options.find((o) => o.id === selected)?.isCorrect === true
    : false;

  const handleSelect = (optionId) => {
    if (revealed) return; // prevent changing after reveal
    setSelected(optionId);
  };

  const revealAnswer = () => {
    if (revealed || selected == null) return;
    const correct = q.options.find((o) => o.id === selected)?.isCorrect;

    // Update stats and xp in memory and localStorage
    const nextStats = { ...stats };
    const d = discipline;
    const cur = nextStats[d] || { attempted: 0, correct: 0 };
    cur.attempted += 1;
    if (correct) cur.correct += 1;
    nextStats[d] = cur;
    setStats(nextStats);
    saveStats(nextStats);

    if (correct) {
      setSessionCorrect((c) => c + 1); // Track session correct count
      const add = BASE_XP_PER_CORRECT;
      setEarnedXp((x) => x + add);
      const newTotal = totalXp + add;
      setTotalXp(newTotal);
      saveXp(newTotal);

      // Fireworks animation from the exact center of the viewport
      scheduleConfetti();
    }

    // Trigger shake animation on wrong answer and delay reveal so it's visible
    if (!correct) {
      validateBtnControls.start({
        x: [0, -8, 8, -6, 6, -3, 3, 0],
        transition: { duration: 0.45, ease: "easeInOut" },
      });
      setTimeout(() => setRevealed(true), 460);
    } else {
      setRevealed(true);
    }
  };

  const nextQuestion = () => {
    if (!revealed) return;
    const next = clamp(currentIdx + 1, 0, questions.length);
    if (next >= questions.length) {
      onFinish({ earnedXp, totalXp, stats, totalQuestions: questions.length, sessionCorrect });
      return;
    }
    setCurrentIdx(next);
    setSelected(null);
    setRevealed(false);
    window.scrollTo(0, 0);
  };

  const getLetter = (i) => String.fromCharCode(65 + i);

  return (
    <div className="blog-grid-wrapper pt-110 quiz-template">
      <div className="container">
        {/* Mini XP/Level badge during quiz */}
        <div className="row">
          <div className="col-12 d-flex justify-content-start">
            {/* <span className="badge quiz-badge quiz-badge--sm me-2">
               {QUIZ_DISCIPLINES.find((d) => d.id === discipline)?.label}
                {" - "}
                {QUIZ_LEVELS.find((l) => l.id === level)?.label}
            </span>
            <span className="badge quiz-badge quiz-badge--sm">
              XP: {totalXp} · Niveau: {getLevelFromXp(totalXp, LEVEL_THRESHOLDS)}
            </span> */}
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="quiz-card qt-card" style={{ minHeight: 'calc(100vh - 110px)', display: 'flex', flexDirection: 'column' }}>
              <div className="qt-card-head">
                <div className="qt-head-top">
                  <span className="qt-head-title">
                    {QUIZ_DISCIPLINES.find((d) => d.id === discipline)?.label}
                  </span>
                  <span className="qt-counter ms-2">({currentIdx + 1}/{questions.length})</span>
                </div>
                {/* Visual Progress Bar */}
                <div className="qt-progress-container">
                  <div className="qt-progress-bar">
                    <motion.div
                      className="qt-progress-fill"
                      initial={{ width: 0 }}
                      animate={{ width: `${((currentIdx + (revealed ? 1 : 0)) / questions.length) * 100}%` }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                    />
                  </div>
                  <div className="qt-progress-steps">
                    {questions.map((_, idx) => (
                      <div
                        key={idx}
                        className={`qt-progress-step ${idx < currentIdx ? 'completed' : ''} ${idx === currentIdx ? 'current' : ''}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="qt-question-box" style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentIdx}
                    variants={questionVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                    style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto' }}
                  >
                    <div className="qt-topline">
                      <h4 className="qt-prompt">{q.prompt}</h4>
                    </div>
                    <div className="list-group quiz-options-grid">
                      {q.options.map((opt, idx) => {
                        const chosen = selected === opt.id;
                        const correct = opt.isCorrect === true;
                        const showCorrect = revealed && correct;
                        const showWrong = revealed && chosen && !correct;
                        const className = [
                          "list-group-item",
                          "list-group-item-action",
                          "quiz-option",
                          chosen ? "is-selected" : "",
                          showCorrect ? "is-correct" : "",
                          showWrong ? "is-wrong" : "",
                        ].filter(Boolean).join(" ");
                        return (
                          <motion.button
                            key={opt.id}
                            custom={idx}
                            variants={optionVariants}
                            initial="initial"
                            animate="animate"
                            className={className}
                            onClick={() => handleSelect(opt.id)}
                            disabled={revealed}
                            aria-pressed={chosen && !revealed}
                            whileHover={!revealed ? { scale: 1.02, transition: { duration: 0.15 } } : {}}
                            whileTap={!revealed ? { scale: 0.98 } : {}}
                          >
                            <span className="qt-letter">{getLetter(idx)}</span>
                            <span className="qt-text">{opt.text}</span>
                            {chosen && !revealed && (
                              <i className="bi bi-check-circle-fill qt-selected-icon ms-2" aria-hidden="true" />
                            )}
                          </motion.button>
                        );
                      })}
                    </div>

                <div className="mt-3 mb-2 d-flex align-items-center gap-2 qt-actions">
                  {!revealed ? (
                    <motion.button
                      className="btn btn-primary"
                      onClick={revealAnswer}
                      disabled={selected == null}
                      animate={validateBtnControls}
                      whileTap={{ scale: 0.98 }}
                    >
                      Valider
                    </motion.button>
                  ) : (
                    <button className="btn btn-primary" onClick={nextQuestion}>
                      {currentIdx + 1 >= questions.length ? "Voir les résultats" : "Suivante"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-quit"
                    aria-label="Quitter le quiz"
                    onClick={onQuit}
                  >
                    Quitter
                  </button>
                </div>
                {revealed && (
                 <div className="mt-3">
                   {isCorrect ? (
                     <div className="alert alert-success">
                       ✅ Bonne réponse ! Vous gagnez +{BASE_XP_PER_CORRECT} XP.
                     </div>
                   ) : (
                     <div className="alert alert-danger">
                       ❌ Mauvaise réponse.
                       {q.explanation && (
                         <div className="mt-2"><strong>Explication:</strong> {q.explanation}</div>
                       )}
                       {/* {q.reference && (
                         <div className="mt-1"><strong>Référence:</strong> {q.reference}</div>
                       )} */}
                     </div>
                   )}
                 </div>
               )}
                  </motion.div>
                </AnimatePresence>
              </div>

               
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ResultsStep = ({ payload, restart }) => {
  const { earnedXp, totalXp, stats, totalQuestions, sessionCorrect = 0 } = payload;
  const level = getLevelFromXp(totalXp, LEVEL_THRESHOLDS);
  
  // Use sessionCorrect for accurate current quiz score (fallback to earnedXp/10 for old sessions)
  const correctCount = sessionCorrect || Math.floor(earnedXp / BASE_XP_PER_CORRECT);
  const scorePercent = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
  
  // Icon mapping for each discipline
  const disciplineIcons = {
    fiqh: 'bi-journal-bookmark',
    aqida: 'bi-heart',
    hadith: 'bi-chat-quote',
    sirah: 'bi-person-badge'
  };
  
  // Determine performance message
  const getPerformanceMessage = (percent) => {
    if (percent >= 90) return { emoji: '🏆', text: 'Excellent !', color: '#fbbf24' };
    if (percent >= 70) return { emoji: '🌟', text: 'Très bien !', color: '#22c55e' };
    if (percent >= 50) return { emoji: '👍', text: 'Bien joué !', color: '#3b82f6' };
    return { emoji: '💪', text: 'Continuez !', color: '#8b5cf6' };
  };
  const performance = getPerformanceMessage(scorePercent);

  return (
    <div className="blog-grid-wrapper pt-110 quiz-template">
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="quiz-card qt-card" style={{ minHeight: 'calc(100vh - 110px)', display: 'flex', flexDirection: 'column' }}>
              <div className="qt-card-head">
                <span className="qt-head-title">Résultats du quiz</span>
              </div>
              <div className="qt-question-box qt-results-box" style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column' }}>
                
                {/* Hero Score Section */}
                <motion.div 
                  className="qt-results-hero"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                >
                  <div className="qt-score-circle">
                    <motion.span 
                      className="qt-score-value"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3, duration: 0.4 }}
                    >
                      {scorePercent}%
                    </motion.span>
                    <span className="qt-score-label">{correctCount}/{totalQuestions}</span>
                  </div>
                  <div className="qt-performance">
                    <span className="qt-performance-emoji">{performance.emoji}</span>
                    <span className="qt-performance-text" style={{ color: performance.color }}>{performance.text}</span>
                  </div>
                </motion.div>

                {/* XP & Level Badges */}
                <motion.div 
                  className="qt-results-badges"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                >
                  <div className="qt-badge-card qt-badge-xp">
                    <i className="bi bi-lightning-charge-fill" />
                    <div className="qt-badge-content">
                      <span className="qt-badge-value">+{earnedXp}</span>
                      <span className="qt-badge-label">XP gagnés</span>
                    </div>
                  </div>
                  <div className="qt-badge-card qt-badge-total">
                    <i className="bi bi-gem" />
                    <div className="qt-badge-content">
                      <span className="qt-badge-value">{totalXp}</span>
                      <span className="qt-badge-label">XP total</span>
                    </div>
                  </div>
                  <div className="qt-badge-card qt-badge-level">
                    <i className="bi bi-star-fill" />
                    <div className="qt-badge-content">
                      <span className="qt-badge-value">{level}</span>
                      <span className="qt-badge-label">Niveau</span>
                    </div>
                  </div>
                </motion.div>

                {/* Stats by Discipline */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.4 }}
                >
                  <h5 className="qt-section-title">
                    <i className="bi bi-bar-chart-fill me-2" />
                    Statistiques par discipline
                  </h5>
                  <div className="row mt-3 qt-stats-grid">
                    {QUIZ_DISCIPLINES.map((d, idx) => {
                      const s = stats[d.id] || { attempted: 0, correct: 0 };
                      const p = pct(s.correct, s.attempted);
                      const iconClass = disciplineIcons[d.id] || 'bi-mortarboard';
                      return (
                        <motion.div 
                          key={d.id} 
                          className="col-md-6 col-lg-3"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.5 + idx * 0.1, duration: 0.3 }}
                        >
                          <div className="qt-stat-tile">
                            <div className="qt-stat-icon">
                              <i className={`bi ${iconClass}`} />
                            </div>
                            <div className="qt-stat-title">{d.label}</div>
                            <div className="qt-stat-percentage">{p}%</div>
                            <div className="qt-stat-meta">
                              <span className="qt-stat-fraction">{s.correct}/{s.attempted}</span>
                            </div>
                            <div className="qt-progress" style={{ '--pct': `${p}%` }}>
                              <div className="qt-progress-bar" />
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>

                {/* Action Button */}
                <motion.div 
                  className="mt-4 d-flex justify-content-center qt-actions"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8, duration: 0.3 }}
                >
                  <button className="btn btn-primary btn-lg qt-restart-btn" onClick={restart}>
                    <i className="bi bi-arrow-repeat me-2" />
                    Refaire un quiz
                  </button>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const QuizPage = () => {
  const [step, setStep] = useState("setup"); // setup | play | results
  const [params, setParams] = useState({ discipline: QUIZ_DISCIPLINES[0].id, level: QUIZ_LEVELS[0].id });
  const [resultsPayload, setResultsPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const start = async ({ discipline, level }) => {
    setParams({ discipline, level });
    setLoading(true);
    setError(null);
    try {
      await fetchAllQuizQuestions();
      setStep("play");
    } catch (e) {
      console.error(e);
      setError("Impossible de charger les questions. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const finish = (payload) => {
    setResultsPayload(payload);
    setStep("results");
  };

  const restart = () => {
    setStep("setup");
  };

  const quit = () => {
    // Return to setup step; keep XP/stats persistent as requested elsewhere
    setStep("setup");
  };

  return (
    <>
      {step === "setup" && <SetupStep onStart={start} />}
      {loading && (
        <div className="blog-grid-wrapper pt-110">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="section-head">
                  <h3>Préparation du quiz…</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {error && (
        <div className="blog-grid-wrapper pt-110">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="alert alert-danger">{error}</div>
              </div>
            </div>
          </div>
        </div>
      )}
      {step === "play" && (
        <QuizStep
          discipline={params.discipline}
          level={params.level}
          onFinish={finish}
          onQuit={quit}
        />
      )}
      {step === "results" && resultsPayload && (
        <ResultsStep payload={resultsPayload} restart={restart} />
      )}
    </>
  );
};

export default QuizPage;
